import { IWidget, IBaseSettings, IUrlParamWidget } from '../../common/core';

export interface ICheckFacetState {
    /**
     * Is widget collapsed
     */
    collapsed?: boolean;

    /**
     * Object with selected options (keys are options, values always true)
     */
    selected?: { [key: string]: true | false };

    /**
     * Nullable selected
     */
    nullable?: false;

    /**
     * All checkbox selected
     */
    all?: false;
}

export enum SortOption {
    termAsc = 'term.asc',
    termDesc = 'term.desc',
    countAsc = 'count.asc',
    countDesc = 'count.desc',
};

export interface ICheckFacetSettings extends IBaseSettings {
    /**
     * Field
     * `string | ((widget) => string)`
     */
    field: string | ((widget) => string),

    /**
     * Name
     * `string | ((widget) => string)`
     */
    name: string | ((widget) => string),

    /**
     * Visibility level
     */
    level?: number;

    /**
     * Primary key
     */
    primkey?: boolean;

    /**
     * 'true' means to skip adding query part and changing state (null means false)
     */
    ignoreQuery?: boolean;

    /**
     * 'false' means to skip calling 'setState' (null means true). state can be saved in 'onChange'
     */
    setState?: boolean;

    /**
     * Callback after change
     * `(widget) => void;`
     */
    onChange?: (widget) => void;

    /**
     * Show all button (null means true)
     */
    showAll?: boolean;

    /**
     * Is radiobutton (null means false)
     * by default will only search for exact match `xxx=yyy` (see radioAllNested to query all nested items)
     */
    radio?: boolean;

    /**
     * Works only when `radio:true` (null means false). True means nested match search (e.g. `xxx:yyy` instead of `xxx=yyy`)
     */
    radioAllNested?: boolean;

    /**
     * Show `Null/Missing` checkbox (null means false), string value means `true` and label override
     */
    showNull?: true | string;

    /**
     * Hide counters at the right side
     */
    hideCounters?: boolean;

    /**
     * All label
     */
    allLabel?: string;

    /**
     * Enables url param support. 
     * true - using widget title
     */
    urlParam?: true | string;

    /**
     * one of term.asc, term.desc, count.asc, count.desc, by default count.asc
     */
    sort?: SortOption;
}

export class CheckFacetData<T> implements IUrlParamWidget {
    collapsed = false;
    terms: T[] = [];
    preventNextChange: boolean | number;

    constructor(public widget: IWidget<CheckFacetData<T>, ICheckFacetSettings, ICheckFacetState>) {

    }

    collapse() {
        if (this.widget['grid']) {
            return;
        }
        this.widget.state.collapsed = !this.widget.state.collapsed;
        if (this.widget.state.collapsed === false) {
            delete this.widget.state.collapsed;
        }

        this.widget.service.setState(true);
    }

    shortNum(num) {
        num = parseFloat(num);
        var result;
        var c = '';

        if (num > 1000000000) {
            let n = num / 1000000000;
            if (num >= 100000000000)
                result = n.toFixed(0);
            else if (num >= 10000000000)
                result = n.toFixed(1);
            else
                result = n.toFixed(2);
            c = 'g';
        } else if (num >= 1000000) {
            let n = num / 1000000;
            if (num >= 100000000)
                result = n.toFixed(0);
            else if (num >= 10000000)
                result = n.toFixed(1);
            else
                result = n.toFixed(2);
            c = 'm';
        } else if (num >= 1000) {
            let n = num / 1000;
            if (num >= 100000)
                result = n.toFixed(0);
            else if (num >= 10000)
                result = n.toFixed(1);
            else
                result = n.toFixed(2);
            c = 'k';
        }
        else
            result = Math.round(num * 1000) / 1000;

        return `${result}${c}`
    }

    // ------------------------- "All" checkbox -----------------------------
    hideCheckboxAll() {
        if (this.widget.settings.showAll === true) {
            return false;
        }
        return this.widget.settings.showAll === false
            || (this.widget.settings.radio && this.widget.settings.showAll !== true)
            || this.terms == null
            || (this.terms.length <= 1 && !this.widget.settings.showNull);
    }

    public showme() {
        var max_level = Math.max.apply(null, this.widget.service.getAllWidgets().map((w) => {
            return w.settings.field && typeof w.data.getSelArray == 'function' && w.data.getSelArray().length ? w.settings.level : -1;
        })) + 1;
        return this.widget.settings.level <= max_level;
    }
    isAllChecked() {
        return Object.keys(this.widget.state.selected || {}).length == 0;
    }
    checkAll() {
        if (!this.isAllChecked()) {
            this.clearCheck();
        }
    }
    clearCheck(changeState = true, clearKeys = true) {
        if (this.getSelArray(clearKeys).length) {
            delete this.widget.state.selected;

            if (changeState) {
                this.widget.data.preventNextChange = true;
                if (this.widget.settings.onChange) {
                    this.widget.settings.onChange(this.widget);
                }
                if (this.widget.settings.setState !== false) {
                    this.widget.service.setState(this.widget.settings.ignoreQuery);
                }
            }
        }
    }

    // true or false
    // null - all keys
    getSelArray(val = true) {
        //helper to get selected values as array
        var selected = this.widget.state.selected || {};
        return Object.keys(selected).filter(key => {
            if (val === null) {
                return true;
            }
            return selected[key] === val;
        });
    }

    /*
     * Label for Null / Missing option
     */
    get allLabel() {
        return this.widget.settings.allLabel || 'All';
    }

    /*
     * Label for Null / Missing option
     */
    get missingLabel() {
        return typeof this.widget.settings.showNull == 'string' ? this.widget.settings.showNull : 'Null / Missing';
    }

    toggleNullable() {
        // `setTimeout` used to fix issue in IE when `checked` goes to not sync state with [checked]
        setTimeout(() => {
            if (this.widget.state.nullable === false) {
                delete this.widget.state.nullable;
            } else {
                this.widget.state.nullable = false;
            }
            if (this.widget.settings.onChange) {
                this.widget.settings.onChange(this.widget);
            }
            if (this.widget.settings.setState !== false) {
                this.widget.service.setState(this.widget.settings.ignoreQuery);
            }
        });
    }

    get field() {
        return this.getField();
    }

    getField() {
        if (typeof this.widget.settings.field == 'function') {
            return this.widget.settings.field(this.widget);
        }
        return this.widget.settings.field;
    }

    get fieldWithoutAt() {
        return this.getFieldWithoutAt();
    }

    getFieldWithoutAt() {
        var res = this.getField();

        var parts = [];
        var rparts = res.split('@');
        rparts.reverse();
        rparts.forEach((part, i) => {
            if (!part) {
                parts[parts.length - 1] = '@' + parts[parts.length - 1];
            } else {
                parts.push(part);
            }
        });
        parts.reverse();
        parts = parts.map((p, i) => {
            if (!i) {
                return p;
            }
            return `@${p}`;
        });

        // "custom_doc_date@sort:term.desc@period:y" => "custom_doc_date@period:y"
        // "sector@sort:term.asc@@flow" => "sector"
        // "status@@chart_name" => "status"
        // "doc_date@period:y@sort:term.desc" => "doc_date@period:y"
        return parts.filter((p, i) => {
            return !i || (p && p.indexOf('@period:') == 0);
        }).join('');
    }

    get name() {
        if (typeof this.widget.settings.name == 'function') {
            return this.widget.settings.name(this.widget);
        }
        return this.widget.settings.name;
    }

    trackByIndex(index) {
        return index;
    }

    getUrlParam(paramName) {
        var params = this.widget.service.getUrlParams();
        return params[paramName];
    }

    getUrlParamName() {
        if (this.widget.settings.urlParam === true) {
            return (this.name || '').toLowerCase();
        } else {
            return this.widget.settings.urlParam || '';
        }
    }

    getUrlValue() {
        var keys = Object.keys(this.widget.state.selected || {});
        if (keys.length) {
            return keys;
        }
    }

    getStateWithoutUrlParam() {
        var state: ICheckFacetState = JSON.parse(JSON.stringify(this.widget.state || {}));
        delete state.selected;
        delete state['widgetName'];

        if (!Object.keys(state).length) {
            return null;
        }

        return state;
    }

    setSelectedFromUrlParams() {
        if (this.widget.settings.urlParam) {
            var paramName = this.getUrlParamName();
            if (paramName) {
                var param = this.getUrlParam(paramName);
                if (param) {
                    if (!this.widget.service.stateService?.getSavedWidgetState(this.widget.id)) {
                        if (this.widget.state.selected) {
                            Object.keys(this.widget.state.selected).forEach(k => {
                                delete this.widget.state.selected[k];
                            })
                        } else {
                            this.widget.state.selected = {};
                        }

                        if (Array.isArray(param)) {
                            param.forEach(v => {
                                this.widget.state.selected[v] = true;
                            });
                        } else {
                            this.widget.state.selected[param] = true;
                        }
                    }
                }
            }
        }
    }

    stateInit() {
        this.setSelectedFromUrlParams();
    }
}
